import type { EmailBlock } from "@/types/email-blocks";

/**
 * Builds hierarchical tree from flat or already-nested array of blocks
 * Sorts by orderIndex at each level
 *
 * If blocks are already nested (childBlocks populated), preserves that structure.
 * If blocks are flat (parentBlockId set, childBlocks empty), builds the tree.
 *
 * @example
 * const flat = [
 *   { id: '1', parentBlockId: null, orderIndex: 0, childBlocks: [] },
 *   { id: '2', parentBlockId: '1', orderIndex: 0, childBlocks: [] }
 * ];
 * const tree = buildBlockTree(flat);
 * // tree[0].childBlocks[0].id === '2'
 */
export function buildBlockTree(blocks: EmailBlock[]): EmailBlock[] {
  if (blocks.length === 0) {
    return [];
  }

  // Check if blocks are already nested (backend sends nested structure)
  const hasNestedChildren = blocks.some(
    (block) => block.childBlocks && block.childBlocks.length > 0
  );

  if (hasNestedChildren) {
    // Blocks are already nested, just sort recursively
    const sortChildren = (blocks: EmailBlock[]) => {
      blocks.sort((a, b) => a.orderIndex - b.orderIndex);
      blocks.forEach((block) => {
        if (block.childBlocks && block.childBlocks.length > 0) {
          sortChildren(block.childBlocks);
        }
      });
    };

    const rootBlocks = blocks.filter((block) => !block.parentBlockId);
    sortChildren(rootBlocks);
    return rootBlocks;
  }

  // Blocks are flat, build tree from parentBlockId relationships
  const blockMap = new Map<string, EmailBlock>();
  const rootBlocks: EmailBlock[] = [];

  // First pass: create map with empty children arrays
  blocks.forEach((block) => {
    blockMap.set(block.id, { ...block, childBlocks: [] });
  });

  // Second pass: build tree
  blocks.forEach((block) => {
    const blockNode = blockMap.get(block.id)!;
    if (block.parentBlockId) {
      const parent = blockMap.get(block.parentBlockId);
      if (parent) {
        parent.childBlocks.push(blockNode);
      }
    } else {
      rootBlocks.push(blockNode);
    }
  });

  // Sort all levels by orderIndex
  const sortChildren = (blocks: EmailBlock[]) => {
    blocks.sort((a, b) => a.orderIndex - b.orderIndex);
    blocks.forEach((block) => {
      if (block.childBlocks && block.childBlocks.length > 0) {
        sortChildren(block.childBlocks);
      }
    });
  };

  sortChildren(rootBlocks);
  return rootBlocks;
}

/**
 * Finds a block by ID in the tree (recursive search)
 * Returns null if not found
 */
export function findBlockById(
  tree: EmailBlock[],
  id: string
): EmailBlock | null {
  for (const block of tree) {
    if (block.id === id) {
      return block;
    }
    if (block.childBlocks && block.childBlocks.length > 0) {
      const found = findBlockById(block.childBlocks, id);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

/**
 * Gets the path from root to a specific block
 * Returns array of block IDs: [rootId, parentId, blockId]
 *
 * @example
 * getBlockPath(tree, 'child-id') // ['root-id', 'parent-id', 'child-id']
 */
export function getBlockPath(tree: EmailBlock[], blockId: string): string[] {
  const findPath = (
    blocks: EmailBlock[],
    targetId: string,
    currentPath: string[]
  ): string[] | null => {
    for (const block of blocks) {
      const newPath = [...currentPath, block.id];
      if (block.id === targetId) {
        return newPath;
      }
      if (block.childBlocks && block.childBlocks.length > 0) {
        const found = findPath(block.childBlocks, targetId, newPath);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };

  return findPath(tree, blockId, []) || [];
}

/**
 * Validates tree structure:
 * - No circular references
 * - All parent IDs point to existing blocks
 * - OrderIndex values are valid (>= 0)
 * - No duplicate IDs
 */
export function validateTree(tree: EmailBlock[]): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];
  const seenIds = new Set<string>();
  const allBlocks: EmailBlock[] = [];

  // Flatten tree to collect all blocks
  const collectBlocks = (blocks: EmailBlock[]) => {
    blocks.forEach((block) => {
      allBlocks.push(block);
      if (block.childBlocks && block.childBlocks.length > 0) {
        collectBlocks(block.childBlocks);
      }
    });
  };
  collectBlocks(tree);

  // Check for duplicate IDs
  allBlocks.forEach((block) => {
    if (seenIds.has(block.id)) {
      errors.push(`Duplicate block ID found: ${block.id}`);
    }
    seenIds.add(block.id);
  });

  // Create a map of all blocks for parent validation
  const blockMap = new Map<string, EmailBlock>();
  allBlocks.forEach((block) => blockMap.set(block.id, block));

  // Validate each block
  allBlocks.forEach((block) => {
    // Check orderIndex
    if (block.orderIndex < 0) {
      errors.push(
        `Invalid orderIndex (${block.orderIndex}) for block ${block.id}`
      );
    }

    // Check parent exists
    if (block.parentBlockId) {
      if (!blockMap.has(block.parentBlockId)) {
        errors.push(
          `Parent block ${block.parentBlockId} not found for block ${block.id}`
        );
      }
    }
  });

  // Check for circular references
  const checkCircular = (
    blockId: string,
    visited: Set<string>
  ): boolean => {
    if (visited.has(blockId)) {
      return true;
    }
    visited.add(blockId);

    const block = blockMap.get(blockId);
    if (block?.parentBlockId) {
      return checkCircular(block.parentBlockId, new Set(visited));
    }
    return false;
  };

  allBlocks.forEach((block) => {
    if (checkCircular(block.id, new Set())) {
      errors.push(`Circular reference detected for block ${block.id}`);
    }
  });

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Reorders blocks within same parent
 * Updates orderIndex values to maintain sequence
 * Returns new tree with updated orderIndex values
 *
 * @example
 * // Move block from index 2 to index 0
 * const newTree = reorderBlocks(tree, 'block-id', 0);
 */
export function reorderBlocks(
  tree: EmailBlock[],
  blockId: string,
  newOrderIndex: number
): EmailBlock[] {
  const updateBlock = (blocks: EmailBlock[]): EmailBlock[] => {
    // Check if the block is at this level
    const blockIndex = blocks.findIndex((b) => b.id === blockId);

    if (blockIndex !== -1) {
      // Block found at this level - reorder siblings
      const newBlocks = [...blocks];
      const [movedBlock] = newBlocks.splice(blockIndex, 1);
      newBlocks.splice(newOrderIndex, 0, movedBlock);

      // Update all orderIndex values
      return newBlocks.map((block, index) => ({
        ...block,
        orderIndex: index,
      }));
    }

    // Block not at this level - recurse into children
    return blocks.map((block) => ({
      ...block,
      childBlocks:
        block.childBlocks && block.childBlocks.length > 0
          ? updateBlock(block.childBlocks)
          : block.childBlocks,
    }));
  };

  return updateBlock(tree);
}

/**
 * Moves block to new parent with new order
 * Handles:
 * - Removing from old parent
 * - Adding to new parent
 * - Reordering siblings in both locations
 *
 * @param newParentId - null for root level
 */
export function moveBlock(
  tree: EmailBlock[],
  blockId: string,
  newParentId: string | null,
  newOrderIndex: number
): EmailBlock[] {
  // First, find the block
  const foundBlock = findBlockById(tree, blockId);
  if (!foundBlock) {
    return tree; // Block not found
  }

  // Remove the block from tree
  const removeBlock = (blocks: EmailBlock[]): EmailBlock[] => {
    const filtered = blocks.filter((b) => b.id !== blockId);
    return filtered.map((block) => ({
      ...block,
      childBlocks:
        block.childBlocks && block.childBlocks.length > 0
          ? removeBlock(block.childBlocks)
          : block.childBlocks,
    }));
  };

  let newTree = removeBlock(tree);

  // Create a new block with updated properties
  const blockToInsert: EmailBlock = {
    ...foundBlock,
    parentBlockId: newParentId || undefined,
    orderIndex: newOrderIndex,
  };

  // Insert the block at the new location
  if (newParentId === null) {
    // Insert at root level
    newTree.splice(newOrderIndex, 0, blockToInsert);
    // Reorder all root blocks
    newTree = newTree.map((block, index) => ({
      ...block,
      orderIndex: index,
    }));
  } else {
    // Insert into parent
    const insertIntoParent = (blocks: EmailBlock[]): EmailBlock[] => {
      return blocks.map((block) => {
        if (block.id === newParentId) {
          const newChildren = [...(block.childBlocks || [])];
          newChildren.splice(newOrderIndex, 0, blockToInsert);
          return {
            ...block,
            childBlocks: newChildren.map((child, index) => ({
              ...child,
              orderIndex: index,
            })),
          };
        }
        return {
          ...block,
          childBlocks:
            block.childBlocks && block.childBlocks.length > 0
              ? insertIntoParent(block.childBlocks)
              : block.childBlocks,
        };
      });
    };

    newTree = insertIntoParent(newTree);
  }

  return newTree;
}

/**
 * Duplicates a block and all its children recursively
 * Generates new IDs for all duplicated blocks
 * Places duplicate after original by default
 *
 * @returns Object with new tree and ID of root duplicated block
 */
export function duplicateBlockTree(
  tree: EmailBlock[],
  blockId: string,
  generateId: () => string = () => crypto.randomUUID()
): { newTree: EmailBlock[]; newBlockId: string } {

  const duplicateBlockRecursive = (block: EmailBlock, parentBlockId = block.parentBlockId): EmailBlock => {
    const newId = generateId();
    const duplicated: EmailBlock = {
      ...block,
      id: newId,
      parentBlockId,
      // Copied children belong to the copy, not to the source block
      childBlocks: block.childBlocks
        ? block.childBlocks.map((child) => duplicateBlockRecursive(child, newId))
        : [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    return duplicated;
  };

  let newBlockId = "";

  const duplicateInTree = (blocks: EmailBlock[]): EmailBlock[] => {
    const result: EmailBlock[] = [];

    for (let i = 0; i < blocks.length; i++) {
      const block = blocks[i];
      result.push(block);

      if (block.id === blockId) {
        // Duplicate this block
        const duplicated = duplicateBlockRecursive(block);
        newBlockId = duplicated.id;
        result.push(duplicated);
      } else if (block.childBlocks && block.childBlocks.length > 0) {
        // Recurse into children
        result[result.length - 1] = {
          ...block,
          childBlocks: duplicateInTree(block.childBlocks),
        };
      }
    }

    // Reorder to fix orderIndex
    return result.map((block, index) => ({
      ...block,
      orderIndex: index,
    }));
  };

  const newTree = duplicateInTree(tree);

  return { newTree, newBlockId };
}

/**
 * Deletes a block and all its children from tree
 * Updates orderIndex of remaining siblings
 */
export function deleteBlock(tree: EmailBlock[], blockId: string): EmailBlock[] {
  const removeBlock = (blocks: EmailBlock[]): EmailBlock[] => {
    const filtered = blocks.filter((b) => b.id !== blockId);

    return filtered.map((block, index) => ({
      ...block,
      orderIndex: index,
      childBlocks:
        block.childBlocks && block.childBlocks.length > 0
          ? removeBlock(block.childBlocks)
          : block.childBlocks,
    }));
  };

  return removeBlock(tree);
}

/**
 * Flattens tree back to array (for API submission)
 * Maintains parent-child relationships via parentBlockId
 *
 * @example
 * const flat = flattenBlockTree(tree);
 * // All blocks in single array, childBlocks arrays empty
 */
export function flattenBlockTree(tree: EmailBlock[]): EmailBlock[] {
  const result: EmailBlock[] = [];

  const flatten = (blocks: EmailBlock[]) => {
    blocks.forEach((block) => {
      const { childBlocks, ...blockWithoutChildren } = block;
      result.push({
        ...blockWithoutChildren,
        childBlocks: [],
      } as EmailBlock);

      if (childBlocks && childBlocks.length > 0) {
        flatten(childBlocks);
      }
    });
  };

  flatten(tree);
  return result;
}

/**
 * Serializes block tree to JSON string
 * Used for versions, templates, custom blocks
 */
export function serializeBlockTree(tree: EmailBlock[]): string {
  return JSON.stringify(tree);
}

/**
 * Deserializes JSON string back to block tree
 * Rebuilds hierarchical structure
 */
export function deserializeBlockTree(json: string): EmailBlock[] {
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed;
  } catch {
    return [];
  }
}

/**
 * Gets all descendant IDs of a block (recursive)
 * Useful for bulk operations
 */
export function getDescendantIds(
  tree: EmailBlock[],
  blockId: string
): string[] {
  const block = findBlockById(tree, blockId);
  if (!block) {
    return [];
  }

  const ids: string[] = [];

  const collectIds = (block: EmailBlock) => {
    if (block.childBlocks && block.childBlocks.length > 0) {
      block.childBlocks.forEach((child) => {
        ids.push(child.id);
        collectIds(child);
      });
    }
  };

  collectIds(block);
  return ids;
}

/**
 * Checks if blockA is an ancestor of blockB
 * Prevents moving block into its own children
 */
export function isAncestor(
  tree: EmailBlock[],
  ancestorId: string,
  descendantId: string
): boolean {
  const path = getBlockPath(tree, descendantId);
  return path.includes(ancestorId);
}

/**
 * Finds the parent block of a given block
 * Returns null if the block is a root block or not found
 */
export function findParentBlock(
  tree: EmailBlock[],
  blockId: string
): EmailBlock | null {
  const findParent = (
    blocks: EmailBlock[],
    targetId: string
  ): EmailBlock | null => {
    for (const block of blocks) {
      // Check if this block has the target as a direct child
      if (block.childBlocks && block.childBlocks.some((child) => child.id === targetId)) {
        return block;
      }
      // Recursively search in children
      if (block.childBlocks && block.childBlocks.length > 0) {
        const found = findParent(block.childBlocks, targetId);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };

  return findParent(tree, blockId);
}
